<template>
  <MainLayout>
    <div class="container mw-960" slot="action-bar">
      <div class="row">
        <div class="col-md-6 col-lg-6 col-xl-7">
          <CardTitleInput
            class="action-header typography--headline"
            v-bind:model-value="card.title"
            v-on:input="applyCardTitle"
            v-on:has-error="onHasError"
          >
          </CardTitleInput>
        </div>

        <div class="col-md-6 col-lg-6 col-xl-5 hidden-sm-down">
          <div class="action-btn-row">
            <MoreBtnDropdown class="float-right">
              <b-dropdown-item
                class="dropdown-item typography--color-secondary"
                @click="$root.$emit('show::modal', 'delete-my-card-modal')"
              >
                {{ $t('common.card.menu.remove') }}
              </b-dropdown-item>
            </MoreBtnDropdown>
            <button
              class="btn btn-primary float-right ml-1 mr-1"
              @click="saveMyCard()"
              :disabled="saveDisabled"
            >
              <span v-if="!busy">{{ $t('common.save') }}</span>
              <LoadingBounce v-else></LoadingBounce>
            </button>
            <button
              class="btn btn-outline-primary float-right mr-1"
              v-on:click="openCardPreview"
              v-bind:disabled="saveDisabled"
            >
              {{ $t('common.preview') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="container mw-960 mt-4 pt-1" slot="layout-body">
      <div class="row">
        <div class="col-md-4">
          <div class="card-row">
            <Card class="library-detail-card">
              <div class="card-cover-img-thumb" v-bind:style="styleBindings"></div>

              <label class="card-cover-img-input text-center clickable">
                <img src="/static/images/ic-camera.svg" alt="">
                <input type="file" class="no-display" accept="image/png,image/jpeg,image/jpg" v-on:change="onCoverImageChange">
              </label>

              <div class="card-block">
                <div class="card-info-section">
                  <div class="row" v-if="!card.subject_id">
                    <div class="col-12 typography--body-3 typography--color-disabled">
                      {{ $t('card_details.card_meta.subject_placeholder') }}
                    </div>
                  </div>

                  <div class="row" v-else>
                    <div class="col-3">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.subject') }}
                      </small>
                    </div>
                    <div class="col-9">
                      <small class="typography--body-3 typography--color-primary">
                        {{ getSubjectLabel(card) }}
                      </small>
                    </div>
                  </div>


                  <div class="row" v-if="card.grade_id === undefined">
                    <div class="col-12 typography--body-3 typography--color-disabled">
                      {{ $t('card_details.card_meta.grade_placeholder') }}
                    </div>
                  </div>

                  <div class="row" v-else>
                    <div class="col-3">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.grade') }}
                      </small>
                    </div>
                    <div class="col-9">
                      <small class="typography--body-3 typography--color-primary">
                        {{ getGradeLabel(card) }}
                      </small>
                    </div>
                  </div>

                  <!-- Unit -->
                  <div class="row" v-if="!card.unit_id">
                    <div class="col-12 typography--body-3 typography--color-disabled">
                      {{ $t('card_details.card_meta.chapter_placeholder') }}
                    </div>
                  </div>

                  <div class="row" v-if="card.unit_id">
                    <div class="col-3">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.chapter') }}
                      </small>
                    </div>
                    <div class="col-9">
                      <small class="typography--body-3 typography--color-primary">
                        {{ card.chapter_name }}
                      </small>
                    </div>
                  </div>

                  <!-- Core Concept -->
                  <div class="row" v-if="card.core_concept_id === undefined">
                    <div class="col-12 typography--body-3 typography--color-disabled">
                      {{ $t('card_details.card_meta.core_concepts_placeholder') }}
                    </div>
                  </div>

                  <div class="row" v-else>
                    <div class="col-3">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.core_concept') }}
                      </small>
                    </div>
                    <div class="col-9">
                      <small class="typography--body-3 typography--color-primary">
                        {{ getCoreConceptLabel(coreConcepts[card.core_concept_id]) }}
                      </small>
                    </div>
                  </div>
                </div>

                <div class="card-keywords-section">
                  <div class="row" v-if="card.core_concept_id">
                    <div class="col-3">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.learning_elements') }}
                      </small>
                    </div>
                    <div class="col-9">
                      <small class="typography--body-3 typography--color-primary">
                        {{ getLearningElements(coreConcepts[card.core_concept_id]) }}
                      </small>
                    </div>
                  </div>
                </div>

              </div>

              <div class="card-action">
                <button
                  class="btn btn-outline-primary btn-block"
                  @click="$root.$emit('show::modal', 'edit-card-modal')"
                >
                  {{ $t('card_details.card_meta.edit') }}
                </button>

                <b-modal id="edit-card-modal"
                         v-bind:title="$t('card_details.card_meta.edit')"
                         class="edit-card-modal"
                         v-on:ok="applySelectedCardInfo"
                         v-on:shown="setupEditCardModal"
                         v-bind:ok-title="$t('common.save')"
                         v-bind:close-title="$t('common.cancel')">

                  <div class="dropdown-group">
                    <div class="form-group">
                      <label for="card-title-input" class="typography--body-3 typography--color-secondary">
                        <strong>{{ $t('card_details.card_meta.title') }}</strong>
                      </label>
                      <input type="text"
                             class="form-control basic-text-input"
                             id="card-title-input"
                             v-bind:placeholder="$t('card_details.card_meta.title_placeholder')"
                             maxlength="50"
                             v-model="selected.title">
                    </div>
                  </div>

                  <div class="dropdown-group" v-if="isPublisherUser">
                    <div class="form-group">
                      <label for="card-goal-input" class="typography--body-3 typography--color-secondary">
                        <strong>{{ $t('card_details.card_meta.goal') }}</strong>
                      </label>
                      <AutosizeTextarea
                        id="card-goal-input"
                        class="form-control basic-text-input"
                        v-model="selected.goal"
                        v-bind:rows="3"
                        v-bind:limit="5000"
                        v-bind:placeholder="$t('card_details.card_meta.goal_placeholder')"
                      />
                    </div>
                  </div>

                  <div class="dropdown-group">
                    <div class="dropdown-label typography--body-3 typography--color-secondary">
                      <strong>{{ $t('card_details.card_meta.grade') }}</strong>
                    </div>
                    <b-dropdown
                      v-bind:text="getTranslatedName(gradeMap[selected.grade_id])"
                      class="edit-card-dropdown"
                    >
                      <b-dropdown-item
                        v-for="(value, key) in gradeList"
                        @click="selectGrade(value.id, value.school_year)"
                        :key="key"
                      >
                        {{ getTranslatedName(gradeMap[value.id]) }}
                      </b-dropdown-item>
                      <b-dropdown-item @click="selectGrade(null, 'null')">
                        {{ $t('common.selects.others') }}
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>

                  <div class="dropdown-group">
                    <div class="dropdown-label typography--body-3 typography--color-secondary">
                      <strong>{{ $t('card_details.card_meta.subject') }}</strong>
                    </div>
                    <b-dropdown
                      id="subject-dropdown"
                      class="edit-card-dropdown"
                      :text="getTranslatedName(subjectMap[selected.subject_id])"
                    >
                      <b-dropdown-item
                        v-for="(value, key) in gradeSubjects"
                        @click="selectSubject(value.id)"
                        :key="key"
                      >
                        <template v-if="value.id">
                          {{ getTranslatedName(subjectMap[value.id]) }}
                        </template>
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>

                  <!-- Units -->
                  <div class="dropdown-group">
                    <div class="dropdown-label typography--body-3 typography--color-secondary">
                      <strong>{{ $t('card_details.card_meta.chapter') }}</strong>
                    </div>

                    <b-dropdown
                      id="unit-dropdown"
                      class="edit-card-dropdown"
                      v-bind:text="getUnitLabel(units[selected.unit_id]) || $t('card_details.card_meta.select')"
                    >
                      <b-dropdown-item v-on:click="selectUnit('9999')">
                        {{ $t('card_details.card_meta.select') }}
                      </b-dropdown-item>

                      <b-dropdown-item v-for="unit in currentUnitList"
                                       v-on:click="selectUnit(unit.id)"
                                       v-bind:key="unit.id">
                        {{ getUnitLabel(unit) }}
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>

                  <!-- Core Concepts -->
                  <div class="dropdown-group">
                    <div class="dropdown-label typography--body-3 typography--color-secondary">
                      <strong>{{ $t('card_details.card_meta.core_concept') }}</strong>
                    </div>

                    <b-dropdown
                      id="core-concept-dropdown"
                      class="edit-card-dropdown"
                      v-bind:text="getDetailedCoreConceptLabel(coreConcepts[selected.core_concept_id]) || $t('card_details.card_meta.select')"
                    >
                      <b-dropdown-item v-on:click="selectCoreConcept('9999')">
                        {{ $t('card_details.card_meta.select') }}
                      </b-dropdown-item>

                      <b-dropdown-item v-for="coreConcept in currentCoreConceptList"
                                      v-on:click="selectCoreConcept(coreConcept.id)"
                                      v-bind:key="coreConcept.id"
                                      v-bind:title="`${$t('card_details.card_meta.learning_elements')}: ${coreConcept.description}`">
                        {{ getDetailedCoreConceptLabel(coreConcept) }}
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>

                  <div class="keyword-group">
                    <div class="keyword-label typography--body-3 typography--color-secondary">
                      <strong>{{ $t('card_details.card_meta.learning_elements') }}</strong>
                    </div>
                    <div class="keyword-container">
                      <small class="typography--body-3 typography--color-secondary" v-if="coreConcepts[selected.core_concept_id]">
                        {{ getLearningElements(coreConcepts[selected.core_concept_id]) }}
                      </small>
                      <small class="typography--body-3 typography--color-disabled" v-else>
                        -
                      </small>
                    </div>
                  </div>

                </b-modal>
              </div>

            </Card>
          </div>
        </div>


        <div class="col-md-8">
          <draggable v-model="card.pages" @start="drag=true" @end="drag=false">
            <div
              class="card-row"
              v-for="(page, index) in card.pages"
              :key="index"
            >
              <MyPageSummary v-bind:page="page"
                             v-bind:index="index"
                             v-bind:card-title="card.title"
                             v-on:edit-page="openPageEditorModal"
                             v-on:remove-page="openRemovePageModal"></MyPageSummary>
            </div>
          </draggable>

          <div class="card-row">
            <PageAddBlock></PageAddBlock>
          </div>
        </div>
      </div>
    </div>

    <div slot="modal-area">
      <BasicModal id="delete-my-card-modal" v-bind:title="$t('common.modal.remove.title')" v-on:ok="deleteCard">
        <div v-html="$t('common.modal.remove.message')"></div>
      </BasicModal>

      <BasicModal id="delete-my-card-page-modal" v-bind:title="$t('common.modal.remove.title')" v-on:ok="removePage" v-on:cancel="cancelRemovePage">
        <div v-html="$t('common.modal.remove.message')"></div>
      </BasicModal>

      <BasicModal id="make-card-public-modal" v-bind:title="$t('common.modal.make_public.title')">
        <div v-html="$t('common.modal.make_public.message')"></div>
      </BasicModal>

      <b-modal id="quiz-type-select-modal"
               class="basic-modal hide-ok-btn square-area-btn-modal"
               v-bind:title="$t('card_details.create_page')"
               v-bind:close-title="$t('common.close')">
        <div class="row square-area-btn-row">
          <div class="col-3 square-area-btn-col">
            <div class="square-area-btn text-center typography--body-3 typography--color-secondary clickable"
                 v-on:click="showNonQuizEditorModal = true;$root.$emit('hide::modal', 'quiz-type-select-modal')">
              <div class="square-area-btn-icon square-area-btn-non-quiz"></div>
              {{ $t('common.page_types.non_quiz') }}
            </div>
          </div>

          <div class="col-3 square-area-btn-col">
            <div class="square-area-btn text-center typography--body-3 typography--color-secondary clickable"
                 v-on:click="showMultipleChoiceEditorModal = true;$root.$emit('hide::modal', 'quiz-type-select-modal')">
              <div class="square-area-btn-icon square-area-btn-multiple-choice"></div>
              {{ $t('common.page_types.multiple_choice') }}
            </div>
          </div>

          <div class="col-3 square-area-btn-col">
            <div class="square-area-btn text-center typography--body-3 typography--color-secondary clickable"
                 v-on:click="showTextEntryEditorModal = true;$root.$emit('hide::modal', 'quiz-type-select-modal')">
              <div class="square-area-btn-icon square-area-btn-text-entry"></div>
              {{ $t('common.page_types.text_entry') }}
            </div>
          </div>

          <div class="col-3 square-area-btn-col">
            <div class="square-area-btn text-center typography--body-3 typography--color-secondary clickable"
                 v-on:click="showEssayTypeEditorModal = true;$root.$emit('hide::modal', 'quiz-type-select-modal')">
              <div class="square-area-btn-icon square-area-btn-essay-type"></div>
              {{ $t('common.page_types.essay_type') }}
            </div>
          </div>
        </div>
      </b-modal>

      <PageLibraryModal v-on:import-pages="importPages"></PageLibraryModal>

      <PageEditorModal id="non_quiz-editor-modal"
                        v-show="showNonQuizEditorModal"
                        new-page-type="non_quiz"
                        v-on:page-added="onPageAdded"
                        v-bind:is-shown="showNonQuizEditorModal"
                        v-on:cancel="showNonQuizEditorModal = false">
      </PageEditorModal>

      <PageEditorModal id="multiple_choice-editor-modal"
                        v-show="showMultipleChoiceEditorModal"
                        new-page-type="multiple_choice"
                        v-on:page-added="onPageAdded"
                        v-bind:is-shown="showMultipleChoiceEditorModal"
                        v-on:cancel="showMultipleChoiceEditorModal = false">
      </PageEditorModal>

      <PageEditorModal id="text_entry-editor-modal"
                        v-show="showTextEntryEditorModal"
                        new-page-type="text_entry"
                        v-on:page-added="onPageAdded"
                        v-bind:is-shown="showTextEntryEditorModal"
                        v-on:cancel="showTextEntryEditorModal = false">
      </PageEditorModal>

      <PageEditorModal id="essay_type-editor-modal"
                        v-show="showEssayTypeEditorModal"
                        new-page-type="essay_type"
                        v-on:page-added="onPageAdded"
                        v-bind:is-shown="showEssayTypeEditorModal"
                        v-on:cancel="showEssayTypeEditorModal = false">
      </PageEditorModal>

      <PageEditorModal id="edit-page-editor-modal"
                        v-show="showEditPageModal"
                        v-bind:page="currentEditingPage"
                        v-on:page-added="onPageAdded"
                        v-bind:is-shown="showEditPageModal"
                        v-on:cancel="cancelPageEdit">
      </PageEditorModal>

      <AddVideoModal v-on:add="addVideo"></AddVideoModal>

      <MathEditorModal id="math-editor-modal" v-on:add="addMath"></MathEditorModal>

      <b-modal id="page-leave-alert"
               class="basic-modal no-header-modal"
               v-bind:hide-header="true"
               v-bind:close-title="$t('common.cancel')"
               v-bind:ok-title="$t('common.confirm')"
               v-on:ok="pageLeaveOk"
               v-on:cancel="pageLeaveCancel">
        <div v-html="$t('common.modal.page_leave_alert.message')"></div>
      </b-modal>
    </div>

    <div slot="floating-action-menu" class="hidden-md-up">
      <FloatingDropup>
        <b-dropdown-item v-on:click="openCardPreview">{{ $t('common.preview') }}</b-dropdown-item>
        <div class="dropdown-divider"></div>
        <b-dropdown-item v-on:click="saveMyCard()" v-bind:disabled="saveDisabled">{{ $t('common.save') }}</b-dropdown-item>
        <b-dropdown-item v-on:click="$root.$emit('show::modal', 'delete-my-card-modal')">{{ $t('common.card.menu.remove') }}</b-dropdown-item>
      </FloatingDropup>
    </div>

  </MainLayout>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { isEmpty, cloneDeep, isEqual, sum, isNil, orderBy } from 'lodash';
  import draggable from 'vuedraggable';
  import labels from '@/mixins/labels';
  import MyPageSummary from '../components/MyPageSummary';
  import PageAddBlock from '../components/PageAddBlock';
  import PageEditorModal from '../components/PageEditorModal';
  import AddVideoModal from '../components/AddVideoModal';
  import MathEditorModal from '../components/MathEditorModal';

  export default {
    name: 'new-my-card',

    components: {
      PageEditorModal,
      MathEditorModal,
      MyPageSummary,
      AddVideoModal,
      PageAddBlock,
      draggable,
    },

    mixins: [
      labels,
    ],

    data() {
      return {
        showNonQuizEditorModal: false,

        showMultipleChoiceEditorModal: false,

        showTextEntryEditorModal: false,

        showEssayTypeEditorModal: false,

        showEditPageModal: false,

        busy: false,

        hasError: false,

        pageIndexToDelete: null,

        currentEditingPage: null,

        acceptableFileTypes: [
          'image/jpg',
          'image/jpeg',
          'image/png',
        ],

        fileSizeLimit: 10 * 1024 * 1024,

        initialCardState: {
          title: '',
          goal: null,
          pages: [],
          subject_id: undefined,
          grade_id: undefined,
          unit_id: undefined,
          chapter_position: undefined,
          chapter_name: undefined,
          core_concept_id: undefined,
          cover_image: null,
          cover_image_file: null,
        },

        card: {
          title: '',
          goal: null,
          pages: [],
          subject_id: undefined,
          grade_id: undefined,
          unit_id: undefined,
          chapter_position: undefined,
          chapter_name: undefined,
          core_concept_id: undefined,
          cover_image: null,
          cover_image_file: null,
        },

        selected: {
          title: '',
          goal: '',
          subject_id: '9999',
          grade_id: '9999',
          unit_id: '9999',
          chapter_position: '9999',
          core_concept_id: '9999',
        },

        destination: null,
      };
    },

    computed: {
      ...mapGetters([
        'activeEditorLocation',
        'currentPublisherInfo',
        'isPublisherUser',
        'coreConceptList',
        'activeEditorId',
        'coreConcepts',
        'currentUser',
        'subjectMap',
        'subjectIds',
        'gradeMap',
        'unitList',
        'units',
        'gradeSubjects',
        'gradeList',
      ]),

      selectedSubjectGrade() {
        return `${this.selected.subject_id}&${this.selected.grade_id}`;
      },

      currentUnitList() {
        return this.unitList.filter(unit => (
          unit.subject_id === parseInt(this.selected.subject_id, 10) &&
          // unit.grade_id === parseInt(this.selected.grade_id, 10) &&
          unit.name !== 'NO NAME YET'
        ));
      },

      currentCoreConceptList() {
        return orderBy(this.coreConceptList.filter(coreConcept => (
          coreConcept.subject_id === parseInt(this.selected.subject_id, 10) &&
          coreConcept.grade_id === parseInt(this.selected.grade_id, 10)
        )), 'chapter_position', 'asc');
      },

      cardAuthorName() {
        if (this.card) {
          return this.card.author_name;
        }

        return '';
      },

      styleBindings() {
        if (this.card && this.card.cover_image) {
          return {
            'background-image': `url('${this.card.cover_image}')`,
          };
        }

        return {};
      },

      saveDisabled() {
        return this.card.pages.length === 0 || this.hasError;
      },
    },

    watch: {
      gradeSubjects(state) {
        if (isEmpty(state)) {
          document.getElementById('subject-dropdown').firstElementChild.disabled = true;
        } else {
          document.getElementById('subject-dropdown').firstElementChild.disabled = false;
        }
      },

      currentCoreConceptList(state) {
        if (isEmpty(state)) {
          document.getElementById('core-concept-dropdown').firstElementChild.disabled = true;
        } else {
          document.getElementById('core-concept-dropdown').firstElementChild.disabled = false;
        }

        return !isEmpty(state);
      },

      currentUnitList(state) {
        if (isEmpty(state)) {
          document.getElementById('unit-dropdown').firstElementChild.disabled = true;
        } else {
          document.getElementById('unit-dropdown').firstElementChild.disabled = false;
        }

        return !isEmpty(state);
      },

      selectedSubjectGrade(state) {
        if (state.match(/^[0-9]+&9999$/) || state.match(/^9999&[0-9]+$/)) {
          return null;
        }

        if (!isNil(state.match(/[0-9]+&[0-9]+/))) {
          // if (this.getSchoolYear(this.gradeMap[this.selected.grade_id]) > 6) {
          //   if (isEmpty(this.currentCoreConceptList)) {
          //     return this.fetchCoreConcepts({
          //       subject_id: this.selected.subject_id,
          //       grade_id: this.selected.grade_id,
          //     });
          //   }

          //   return null;
          // }

          return Promise.all([
            this.fetchUnits({
              subject_id: this.selected.subject_id,
              grade_id: this.selected.grade_id,
            }),
            this.fetchCoreConcepts({
              subject_id: this.selected.subject_id,
              grade_id: this.selected.grade_id,
            }),
          ]);
        }

        return null;
      },
    },

    methods: {
      ...mapActions([
        'setSelectedPreviewPageIndex',
        'uploadNewCardCoverImage',
        'uploadNewCardPageImage',
        'uploadNewCardPageAudio',
        'setSelectedPreviewCard',
        'setPageLibraryFilter',
        'createNewMyCardPage',
        'fetchCoreConcepts',
        'updateMyCardPages',
        'createNewMyCard',
        'updateMyPage',
        'fetchUnits',
        'fetchGradeSubjects',
        'resetGradeSubjects',
      ]),

      onPageAdded(newQuizPage) {
        const newPageClone = cloneDeep(newQuizPage);

        if (this.pageIndexToDelete !== null) {
          this.card.pages.splice(this.pageIndexToDelete, 1, newPageClone);
          this.currentEditingPage = null;
          this.pageIndexToDelete = null;
        } else {
          newPageClone.position = this.card.pages.length + 1;
          this.card.pages.push(newPageClone);
        }

        this.showMultipleChoiceEditorModal = false;
        this.showTextEntryEditorModal = false;
        this.showEssayTypeEditorModal = false;
        this.showNonQuizEditorModal = false;
        this.showEditPageModal = false;
      },

      addVideo(embed) {
        let content = '';
        if (embed.is_iframe) {
          content = `<p><iframe src="${embed.src}" width="640" height="360" frameborder="0" scrolling="no" webkitallowfullscreen mozallowfullscreen allowfullscreen></iframe></p><p></p>`;
        } else {
          content = `<p>동영상 링크</p><br>${embed.src}<p></p>`;
        }

        if (this.activeEditorId) {
          const editor = tinymce.get(this.activeEditorId);

          if (this.activeEditorLocation) {
            editor.selection.setRng(this.activeEditorLocation);
          }

          return editor.insertContent(content);
        }

        return tinymce.activeEditor.insertContent(content);
      },

      addMath(latex) {
        if (latex.length > 0) {
          const replacedLatex = latex.replace(/overarc/g, 'overparen');
          const mathInlineId = Math.floor(Math.random() * 1000);
          const content = `&nbsp;<span id="math-inline-${mathInlineId}" contenteditable="false">\\(${replacedLatex}\\)</span>&nbsp;`;
          let activeEditor;

          if (this.activeEditorId) {
            activeEditor = tinymce.get(this.activeEditorId);

            if (this.activeEditorLocation) {
              activeEditor.selection.setRng(this.activeEditorLocation);
            }
          } else {
            activeEditor = tinymce.activeEditor;
          }

          activeEditor.insertContent(content);

          setTimeout(() => {
            /* eslint-disable new-cap */
            MathJax.Hub.Queue(['Typeset', MathJax.Hub, `math-inline-${mathInlineId}`]);
            /* eslint-enable */
            activeEditor.insertContent('');
          }, 200);
        }
      },

      pageLeaveOk() {
        this.$router.push(this.destination.path);
      },

      pageLeaveCancel() {
        this.destination = null;
      },

      onCoverImageChange(event) {
        const images = event.target.files || event.dataTransfer.files;

        if (!images.length) {
          return null;
        }

        const file = images[0];

        if (!file || file.size === undefined || file.type === undefined) {
          return null;
        }

        if (file.size > this.fileSizeLimit) {
          // alert file size
          return this.$root.$emit('show::modal', 'file-size-alert');
        }

        if (this.acceptableFileTypes.indexOf(file.type) === -1) {
          // alert wrong file type
          return this.$root.$emit('show::modal', 'file-type-alert');
        }

        return this.createMedia(file, 'image');
      },

      createMedia(file) {
        if (!window.FileReader) {
          return this.$root.$emit('show::modal', 'file-reader-unsupported-alert');
        }

        const reader = new window.FileReader();

        reader.onload = (event) => {
          this.card.cover_image = event.target.result;
          this.card.cover_image_file = file;
        };

        return reader.readAsDataURL(file);
      },

      selectSubject(subjectId) {
        if (this.selected.subject_id !== subjectId) {
          this.selected.subject_id = subjectId;
          this.selected.core_concept_id = '9999';
          this.selected.unit_id = '9999';
        }
      },

      selectGrade(gradeId, schoolYear) {
        if (this.selected.grade_id !== gradeId) {
          this.selected.grade_id = gradeId;
          this.selected.core_concept_id = '9999';
          this.selected.unit_id = '9999';
          this.selected.subject_id = '9999';
          this.fetchGradeSubjects({ grade: schoolYear });
        }
      },

      selectUnit(unitId) {
        if (this.selected.unit_id !== unitId) {
          this.selected.unit_id = unitId;

          const unit = this.units[unitId];

          if (!isNil(unit) && !isNil(unit.core_concept_id)) {
            this.selected.core_concept_id = unit.core_concept_id;
          }
        }
      },

      selectCoreConcept(coreConceptId) {
        if (this.selected.core_concept_id !== coreConceptId) {
          this.selected.core_concept_id = coreConceptId;
        }
      },

      applySelectedCardInfo() {
        this.card.title = this.selected.title;
        this.card.goal = this.selected.goal || null;

        if (this.selected.subject_id !== '9999') {
          this.card.subject_id = this.selected.subject_id;
        } else {
          this.card.subject_id = undefined;
        }

        this.setPageLibraryFilter({ property: 'subject_id', value: this.card.subject_id || '0' });

        if (this.selected.grade_id !== '9999') {
          this.card.grade_id = this.selected.grade_id;
        } else {
          this.card.grade_id = undefined;
        }

        this.setPageLibraryFilter({ property: 'grade_id', value: this.card.grade_id || '0' });

        if (this.selected.unit_id !== '9999') {
          this.card.unit_id = this.selected.unit_id;

          const unit = this.units[this.card.unit_id];

          this.card.chapter_position = unit ? unit.position : undefined;
          this.card.chapter_name = unit ? unit.name : undefined;
        } else {
          this.card.chapter_position = undefined;
          this.card.chapter_name = undefined;
          this.card.unit_id = undefined;
        }

        if (this.selected.core_concept_id !== '9999') {
          this.card.core_concept_id = this.selected.core_concept_id;
        } else {
          this.card.core_concept_id = undefined;
        }

        if (this.card.unit_id) {
          this.setPageLibraryFilter({ property: 'unit_id', value: this.card.unit_id || '0' });
        } else {
          this.setPageLibraryFilter({ property: 'core_concept_id', value: this.card.core_concept_id || '0' });
        }
      },

      setupEditCardModal() {
        if (this.card.title) {
          this.selected.title = this.card.title;
        } else {
          this.selected.title = '';
        }

        if (this.card.goal) {
          this.selected.goal = this.card.goal;
        } else {
          this.selected.goal = '';
        }

        if (this.card.subject_id) {
          this.selected.subject_id = this.card.subject_id;
        } else {
          this.selected.subject_id = '9999';
        }

        if (this.card.grade_id !== undefined) {
          this.selected.grade_id = this.card.grade_id;
        } else {
          this.selected.grade_id = '9999';
        }

        if (this.card.unit_id) {
          this.selected.unit_id = this.card.unit_id;
        } else {
          this.selected.unit_id = '9999';
        }

        if (this.card.core_concept_id) {
          this.selected.core_concept_id = this.card.core_concept_id;
        } else {
          this.selected.core_concept_id = '9999';
        }
      },

      importPages(pageList) {
        this.card.pages.push(...pageList);
      },

      removePage() {
        if (this.pageIndexToDelete !== null) {
          this.card.pages.splice(this.pageIndexToDelete, 1);
          this.pageIndexToDelete = null;
        }
      },

      cancelRemovePage() {
        this.pageIndexToDelete = null;
      },

      openRemovePageModal(pageIndex) {
        this.pageIndexToDelete = pageIndex;

        this.$root.$emit('show::modal', 'delete-my-card-page-modal');
      },

      openPageEditorModal(pageIndex) {
        this.currentEditingPage = this.card.pages[pageIndex];
        this.pageIndexToDelete = pageIndex;

        this.showEditPageModal = true;
      },

      cancelPageEdit() {
        this.showEditPageModal = false;
        this.currentEditingPage = null;
        this.pageIndexToDelete = null;
      },

      applyCardTitle(inputText) {
        this.card.title = inputText;
      },

      onHasError(hasError) {
        this.hasError = hasError;
      },

      deleteCard() {
        this.$router.push('/my-cards');
      },

      openCardPreview() {
        const cardClone = cloneDeep(this.card);

        this.setSelectedPreviewCard(cardClone)
          .then(() => (
            this.setSelectedPreviewPageIndex(0)
          ))
          .then(() => {
            this.$root.$emit('show::modal', 'card-preview-modal');
          });
      },

      getLearningElements(coreConcept) {
        if (!coreConcept) {
          return null;
        }

        return coreConcept.description;
      },

      saveMyCard() {
        if (!this.busy) {
          this.busy = true;

          const imageCount = sum(this.card.pages.map(page => (page.image_count || 0)));
          const audioCount = sum(this.card.pages.map(page => (page.audio_count || 0)));
          const videoCount = sum(this.card.pages.map(page => (page.video_count || 0)));

          const newCardInfo = {
            title: this.card.title || '제목없음',
            goal: this.card.goal,
            subject_id: this.card.subject_id,
            grade_id: this.card.grade_id,
            unit_id: this.card.unit_id,
            chapter_position: this.card.chapter_position,
            chapter_name: this.card.chapter_name,
            core_concept_id: this.card.core_concept_id,
            author_id: this.currentUser.id,
            author_name: this.currentUser.name,
            publisher_id: this.currentPublisherInfo.id,
            publisher_name: this.currentPublisherInfo.name,
            share_level: 2,
            page_count: this.card.pages.length,
            quiz_count: this.card.pages.filter(page => (!!page.is_quiz)).length,
            image_count: imageCount,
            audio_count: audioCount,
            video_count: videoCount,
            is_autograde: true,
            language: 'KR',
            school_id: this.currentUser.school_id,
          };

          // TODO handle error case
          // 1. Create a new card
          this.createNewMyCard(newCardInfo)
            // 2. Upload cover image if present
            .then((newCard) => {
              if (!this.card.cover_image_file) {
                return newCard;
              }

              const coverImageInfo = {
                cardId: newCard.id,
                imageFile: this.card.cover_image_file,
              };

              return this.uploadNewCardCoverImage(coverImageInfo)
                .then(() => (newCard));
            })
            // 3. update card pages if present
            .then((newCard) => {
              if (this.card.pages.length === 0) {
                return newCard;
              }

              const pageUpdateList = this.card.pages.map((page, i) => {
                if (page.id) {
                  return {
                    type: 'new',
                    store_page_id: page.id,
                    position: i + 1,
                  };
                }

                return {
                  ...page,

                  type: 'created',
                  position: i + 1,
                };
              });
              const createdPageList = pageUpdateList.filter(page => (page.type === 'created'));

              const pageUpdateInfo = {
                cardId: newCard.id,
                pageUpdateList: pageUpdateList.filter(page => (page.type === 'new')),
              };

              if (pageUpdateInfo.pageUpdateList.length > 0) {
                return this.updateMyCardPages(pageUpdateInfo)
                  .then(() => ({ newCard, createdPageList }));
              }

              return { newCard, createdPageList };
            })
            .then(({ newCard, createdPageList }) => {
              // 4. create new pages
              const createPagePromises = createdPageList.map(page => (
                this.createNewMyCardPage({ cardId: newCard.id, page: { ...page, images: [] } })
                  .then((newPage) => {
                    const updateInfo = {
                      content: newPage.content,
                      description: newPage.description || '',
                    };

                    // 4.1 upload page images
                    const uploadImgPromises = page.images.map(image => (
                      this.uploadNewCardPageImage({ pageId: newPage.id, imageFile: image.image_file })
                        .then((newImage) => {
                          if (updateInfo.content.indexOf(`data-image-id="${image.key}"`) !== -1) {
                            updateInfo.content = updateInfo.content
                              .replace(`data-image-id="${image.key}"`, `data-image-id="${newImage.id}"`)
                              .replace(image.image_url, newImage.image_url);
                          }

                          if (updateInfo.description.indexOf(`data-image-id="${image.key}"`) !== -1) {
                            updateInfo.description = updateInfo.description
                              .replace(`data-image-id="${image.key}"`, `data-image-id="${newImage.id}"`)
                              .replace(image.image_url, newImage.image_url);
                          }
                        })
                    ));

                    // 4.2 upload page images
                    const uploadAudioPromises = page.audios.map(audio => (
                      this.uploadNewCardPageAudio({ pageId: newPage.id, audioFile: audio.audio_file })
                    ));

                    return Promise.all(uploadImgPromises)
                      .then(() => (
                        Promise.all(uploadAudioPromises)
                      ))
                      .then(() => {
                        if (isEmpty(updateInfo)) {
                          return Promise.resolve();
                        }

                        // 4.3 update page content
                        return this.updateMyPage({ pageId: newPage.id, updateInfo });
                      });
                  })
              ));

              return Promise.all(createPagePromises)
                .then(() => (newCard));
            })
            // 5. redirect to card edit page
            .then((newCard) => {
              this.initialCardState = cloneDeep(this.card);
              this.busy = false;

              return this.$router.push(`/my-cards/${newCard.id}`);
            })
            .catch((err) => {
              this.busy = false;
              this.$root.$emit('show::modal', 'basic-error-alert');
              return Promise.reject(err);
            });
        }
      },
    },

    beforeRouteLeave(to, from, next) {
      if (!this.destination && !isEqual(this.initialCardState, this.card)) {
        this.destination = to;
        this.$root.$emit('show::modal', 'page-leave-alert');

        return next(false);
      }

      return next();
    },

    mounted() {
      if (isEmpty(this.gradeSubjects)) {
        document.getElementById('subject-dropdown').firstElementChild.disabled = true;
      } else {
        document.getElementById('subject-dropdown').firstElementChild.disabled = false;
      }

      if (isEmpty(this.currentCoreConceptList)) {
        document.getElementById('core-concept-dropdown').firstElementChild.disabled = true;
      } else {
        document.getElementById('core-concept-dropdown').firstElementChild.disabled = false;
      }

      if (isEmpty(this.currentUnitList)) {
        document.getElementById('unit-dropdown').firstElementChild.disabled = true;
      } else {
        document.getElementById('unit-dropdown').firstElementChild.disabled = false;
      }

      this.$root.$emit('show::modal', 'edit-card-modal');
    },

    created() {
      this.resetGradeSubjects();
    },
  };
</script>



// WEBPACK FOOTER //
// src/app/my-cards/views/NewMyCard.vue